// oxlint-disable exhaustive-deps
import { Activity, useCallback, useEffect, useState } from 'react'
import {
  Box,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Snackbar,
  Tab,
  Typography,
} from '@mui/material'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import DateRangeSelector from '../../components/DateRangeSelector'
import DateTimeDisplay from '../../components/DateTimeDisplay'
import DoubleArrowTwoTone from '@mui/icons-material/DoubleArrowTwoTone'
import HoloTable from '../../components/HoloTable'
import { LocalizationProvider } from '@mui/x-date-pickers'
import MuiAlert from '@mui/material/Alert'
import SearchSelect from '../../components/SearchSelect'
import SourceTwoTone from '@mui/icons-material/SourceTwoTone'
import { LoadingButton } from '@mui/lab'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { formatDateTime } from '../../utilities/formatDateTime'
import { getGridSingleSelectOperators } from '@mui/x-data-grid'
import setUrlParams from '../../utilities/setUrlParams'
import useTableControls from '../../hooks/useTableControls'
import { useUser } from '../../context/CurrentUserContext'

export default function ManualTriggerPage() {
  const singleSelectOperators = getGridSingleSelectOperators()
  const typeFilterOperators = singleSelectOperators.filter(
    (operator) => operator.value === 'is' || operator.value === 'isAnyOf',
  )

  // User
  const { user } = useUser()
  const userTimezone = user.settings.timezone || 'UTC'
  const userDateFormat = user.settings.dateFormat || 'iso'

  /**
   * one/several files -> "recording"
   * one/several recording -> "burst"
   * one/several bursts -> "observation"
   * one/several observations -> "collection"
   * one/several collections are part of single "experiment".
   */
  const temporalGroups = [
    'collection',
    'observation',
    'burst',
    'recording',
    'file',
  ]

  // Temporal groups
  const [rawTemporalGroupsData, setRawTemporalGroupsData] = useState([])
  const rawTemporalGroupsDataTableControls = useTableControls([
    { field: 'timestamp', sort: 'desc' },
  ])
  const [
    rawTemporalGroupsDataTotalItemsCount,
    setRawTemporalGroupsDataTotalItemsCount,
  ] = useState(0)

  // Experiments
  const [experiments, setExperiments] = useState([])
  const experimentsControls = useTableControls(
    [{ field: 'name', sort: 'desc' }],
    { page: 0, pageSize: 5 },
  )
  const [totalExperimentsCount, setTotalExperimentsCount] = useState(0)
  const [selectedExperiment, setSelectedExperiment] = useState(null)
  const dataSelectionTableControls = useTableControls([
    { field: 'timestamp', sort: 'desc' },
  ])

  // Date range selector
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [timeRange, setTimeRange] = useState('manualTriggerTimeRange', {
    startTime: 0,
    endTime: 0,
  })

  const resetTimeRange = () => setTimeRange({ startTime: 0, endTime: 0 })

  // Device
  const [devices, setDevices] = useState([])
  const [selectedDevice, setSelectedDevice] = useState(null)

  // Data items (files)
  const [additionalData, setAdditionalData] = useState([])
  const [totalItemsCount, setTotalItemsCount] = useState(0)

  // Analysis Chains
  const [analysisChains, setAnalysisChains] = useState([])
  const [selectedAnalysisChain, setSelectedAnalysisChain] = useState([])

  const handleDeleteChip = (chipToDelete) => () => {
    setSelectedAnalysisChain((chains) =>
      chains.filter((chain) => chain !== chipToDelete),
    )
  }

  const [selectedTab, setSelectedTab] = useState('burst')

  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  })

  const [dataTypes, setDataTypes] = useState([])
  const [selectedDatatypeIds, setSelectedDatatypeIds] = useState([])

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  })

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity })
  }

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackbar({ ...snackbar, open: false })
  }

  const temporalGroupColumns = [
    {
      field: '_id',
      headerName: 'Id',
      filterable: false,
      sortable: true,
      minWidth: 500,
      flex: 1,
    },
    {
      field: 'timestamp',
      headerName: 'Date & Time',
      renderCell: (params) => {
        const date = new Date(Number(params.row.timestamp) / 1000000)
        return <DateTimeDisplay date={date} showTime={true} />
      },
      sortComparator: (v1, v2) => new Date(v2) - new Date(v1),
      minWidth: 500,
      flex: 2,
      hideable: false,
      sortable: true,
      filterable: false,
    },
    {
      field: 'datatype_id',
      headerName: 'Type',
      flex: 1,
      hideable: false,
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      operators: typeFilterOperators,
      valueOptions: dataTypes.map((type) => ({
        value: type.uuid,
        label: type.name,
      })),
      valueGetter: (value, row) => {
        return row.type_name
      },
      renderCell: (params) => {
        const type = dataTypes.find(
          (type) => type._id === params.row.datatype_id,
        )
        return type ? type.name : '-'
      },
    },
  ]

  const handleChange = (event, newValue) => {
    setSelectedTab(newValue)
  }

  useEffect(() => {
    setSelectedExperiment(null)
    resetTimeRange()
    setStartDate(null)
    setEndDate(null)
    setSelectedAnalysisChain([])
    setRowSelectionModel({
      type: 'include',
      ids: new Set(),
    })
  }, [selectedDevice])

  const getExperimentId = (experiment) => {
    if (typeof experiment === 'object' && experiment !== null) {
      return experiment.id || experiment._id
    }
    return experiment
  }

  const fetchWithDate = (url, startDate, endDate) => {
    let dateParams = ''
    if (startDate && endDate) {
      dateParams = `startDate=${startDate.format(
        'YYYY-MM-DD',
      )}&endDate=${endDate.format('YYYY-MM-DD')}`
    }
    return fetchWithAuth(`${url}${dateParams}`)
  }

  const fetchExperimentNames = async (
    page,
    pageSize,
    sortModel,
    filterModel,
  ) => {
    if (!selectedDevice) return

    try {
      const params = setUrlParams(page, pageSize, sortModel, filterModel)

      const { data: experimentsData } = await fetchWithDate(
        `devices/${selectedDevice.uuid}/experiments?${params.toString()}&`,
        startDate,
        endDate,
      )

      setExperiments(
        experimentsData.data.map((exp) => ({
          ...exp,
          uuid: exp._id,
          id: exp._id,
        })),
      )

      setTotalExperimentsCount(experimentsData.pagination.totalItems)
    } catch (error) {
      console.error('Error fetching analyses data:', error)
    }
  }

  const fetchRawFiles = async (page, pageSize, sortModel, filterModel) => {
    if (!selectedDevice || !selectedExperiment) return
    try {
      const params = setUrlParams(page, pageSize, sortModel, filterModel)

      const { data } = await fetchWithAuth(
        `devices/${selectedDevice.uuid}/raw-files?experiment_id=${encodeURIComponent(
          getExperimentId(selectedExperiment),
        )}&${params.toString()}`,
      )
      const rawFilesData = data?.data

      if (rawFilesData) {
        setAdditionalData(() =>
          rawFilesData.map((i) => ({
            ...i,
            uuid: i._id,
            id: i._id,
          })),
        )
        setTotalItemsCount(() => data.pagination.totalItems)
      }
    } catch (error) {
      console.error('Error fetching data items:', error)
    }
  }

  const fetchRawTemporalGroupsData = async (
    page,
    pageSize,
    sortModel,
    filterModel,
  ) => {
    if (!selectedDevice || !selectedExperiment) return
    try {
      const params = setUrlParams(page, pageSize, sortModel, filterModel)

      const { data } = await fetchWithAuth(
        `raw-temporal-groups?model=${selectedTab}&device_id=${selectedDevice._id}&experiment_id=${encodeURIComponent(
          getExperimentId(selectedExperiment),
        )}&${params.toString()}`,
      )
      const temporalGroupData = data?.data

      if (temporalGroupData) {
        setRawTemporalGroupsData(() =>
          temporalGroupData.map((i) => ({
            ...i,
            uuid: i._id,
            id: i._id,
          })),
        )
        setRawTemporalGroupsDataTotalItemsCount(
          () => data.pagination.totalItems,
        )
      }
    } catch (error) {
      console.error(
        'Error fetching data for the selected temporal group:',
        error,
      )
    }
  }

  const fetchDevices = useCallback(async () => {
    try {
      const { data } = await fetchWithAuth('devices')

      setDevices(data.data)
    } catch (error) {
      console.error('Error fetching devices:', error)
    } finally {
    }
  }, [])

  useEffect(() => {
    fetchDevices()
  }, [fetchDevices])

  // Fetch Experiments
  useEffect(() => {
    if (selectedDevice && experimentsControls?.paginationModel?.pageSize) {
      fetchExperimentNames(
        experimentsControls.paginationModel.page,
        experimentsControls.paginationModel.pageSize,
        experimentsControls.sortModel,
        experimentsControls.filterModel,
      )
    }
  }, [
    selectedDevice,
    experimentsControls.paginationModel.page,
    experimentsControls.paginationModel.pageSize,
    experimentsControls.sortModel,
    experimentsControls.filterModel,
    startDate,
    endDate,
  ])

  // Fetch Analysis Chains
  useEffect(() => {
    setSelectedAnalysisChain([])

    const fetchAnalysisChains = async () => {
      if (
        selectedDevice &&
        selectedExperiment &&
        selectedDatatypeIds.length > 0
      ) {
        let url = `analysis-chains?experiment=${selectedExperiment.id}`

        const dataTypeIdsParam = selectedDatatypeIds
          .map((id) => `input_datatype_ids[isAnyOf]=${encodeURIComponent(id)}`)
          .join('&')
        url += `&${dataTypeIdsParam}`

        const { data } = await fetchWithAuth(url)

        setAnalysisChains(data.data)
      }
    }
    fetchAnalysisChains()
  }, [selectedDevice, selectedExperiment, selectedDatatypeIds])

  // Reset time range selector when device or experiment changes
  useEffect(() => {
    if (
      (timeRange.startTime > 0 || timeRange.endTime > 0) &&
      selectedDevice &&
      selectedExperiment
    ) {
      resetTimeRange()
    }
    setSelectedAnalysisChain([])
    setRowSelectionModel({
      type: 'include',
      ids: new Set(),
    })
  }, [selectedExperiment, selectedDevice])

  // Fetch raw files
  useEffect(() => {
    if (selectedDevice && selectedExperiment) {
      fetchRawFiles(
        dataSelectionTableControls.paginationModel.page,
        dataSelectionTableControls.paginationModel.pageSize,
        dataSelectionTableControls.sortModel,
        dataSelectionTableControls.filterModel,
      )
    }
  }, [
    selectedDevice,
    selectedExperiment,
    dataSelectionTableControls.paginationModel.page,
    dataSelectionTableControls.paginationModel.pageSize,
    dataSelectionTableControls.sortModel,
    dataSelectionTableControls.filterModel,
  ])

  // Fetch temporal groups
  useEffect(() => {
    if (
      selectedDevice &&
      selectedExperiment &&
      temporalGroups.includes(selectedTab)
    ) {
      fetchRawTemporalGroupsData(
        rawTemporalGroupsDataTableControls.paginationModel.page,
        rawTemporalGroupsDataTableControls.paginationModel.pageSize,
        rawTemporalGroupsDataTableControls.sortModel,
        rawTemporalGroupsDataTableControls.filterModel,
      )
    }
  }, [
    selectedDevice,
    selectedExperiment,
    selectedTab,
    rawTemporalGroupsDataTableControls.paginationModel.page,
    rawTemporalGroupsDataTableControls.paginationModel.pageSize,
    rawTemporalGroupsDataTableControls.sortModel,
    rawTemporalGroupsDataTableControls.filterModel,
  ])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await fetchWithAuth('data-types')

        setDataTypes(data.data)
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }

    fetchData()
  }, [selectedExperiment])

  useEffect(() => {
    const getSelectedDatatypeIds = () => {
      if (rowSelectionModel.ids.size === 0) {
        setSelectedDatatypeIds([])
        return
      }

      const selectedIds = Array.from(rowSelectionModel.ids)
      const selectedDatatypes = rawTemporalGroupsData
        .filter((row) => selectedIds.includes(row.uuid))
        .map((row) => row.datatype_id)

      const uniqueDatatypeIds = [...new Set(selectedDatatypes)]
      const datatypeUuids = dataTypes
        .filter((type) => uniqueDatatypeIds.includes(type._id))
        .map((type) => type.uuid)

      setSelectedDatatypeIds(datatypeUuids)
    }

    getSelectedDatatypeIds()
  }, [rowSelectionModel, rawTemporalGroupsData, dataTypes])

  const handleRowClick = (row) => {
    setSelectedExperiment(row.row)
  }

  const handleSubmit = async () => {
    if (
      selectedAnalysisChain.length === 0 ||
      rowSelectionModel.ids.size === 0
    ) {
      showSnackbar(
        'Please select at least one analysis chain and one data item.',
        'warning',
      )
      return
    }

    setIsSubmitting(true)
    try {
      const payload = {
        chainIds: selectedAnalysisChain,
        itemIds: Array.from(rowSelectionModel.ids),
        deviceId: selectedDevice._id,
        temporalGroup: selectedTab,
      }

      const { data, status } = await fetchWithAuth('analysis-chains/trigger', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (status === 500) {
        showSnackbar('Failed to send manual trigger request.', 'error')
        return
      }
      showSnackbar('Manual trigger request sent successfully!', 'success')
    } catch (error) {
      console.error('Error triggering analysis chain:', error)
      showSnackbar('Failed to send manual trigger request.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid container size={12} spacing={3}>
        <Grid size={12}>
          <Typography variant="h6" margin={'auto'} justifyContent={'start'}>
            Manual Trigger
          </Typography>
          <Divider sx={{ p: 2 }} />
        </Grid>
        <Grid container size={12}>
          <Grid size={4}>
            {devices && (
              <SearchSelect
                options={devices}
                label="Device"
                selectedValue={selectedDevice}
                onSelectionChange={setSelectedDevice}
                sx={{ mr: 2 }}
                size="medium"
              />
            )}
          </Grid>
        </Grid>

        <Activity mode={selectedDevice ? 'visible' : 'hidden'}>
          <Grid size={12}>
            <Paper sx={{ padding: 2 }}>
              <DateRangeSelector
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                userDateFormat={userDateFormat}
                userTimezone={userTimezone}
              />
            </Paper>
          </Grid>
          <Grid size={12} height={'100%'}>
            <Paper sx={{ padding: 2, width: '100%', height: '100%' }}>
              {experiments && (
                <HoloTable
                  className="experiments-table"
                  id="experiments"
                  selectedRow={getExperimentId(selectedExperiment)}
                  data={experiments}
                  pageSize={5}
                  columns={[
                    {
                      field: 'name',
                      headerName: 'Name',
                      flex: 2,
                      filterable: true,
                    },
                    {
                      field: 'first_timestamp',
                      headerName: 'First data',
                      flex: 1,
                      filterable: true,
                      valueGetter: (params) => {
                        let formattedTime = formatDateTime(
                          params / 1000000,
                          userTimezone,
                          userDateFormat,
                        )
                        return formattedTime
                      },
                    },
                    {
                      field: 'last_timestamp',
                      headerName: 'Newest data',
                      flex: 1,
                      filterable: true,
                      valueGetter: (params) => {
                        return formatDateTime(
                          params / 1000000,
                          userTimezone,
                          userDateFormat,
                        )
                      },
                    },
                  ]}
                  sortBy="exp_name"
                  localeText={{
                    noRowsLabel: 'No experiment found on this device',
                  }}
                  onRowClick={handleRowClick}
                  paginationModel={experimentsControls.paginationModel}
                  onPaginationModelChange={
                    experimentsControls.setPaginationModel
                  }
                  sortModel={experimentsControls.sortModel}
                  onSortModelChange={experimentsControls.setSortModel}
                  filterModel={experimentsControls.filterModel}
                  onFilterModelChange={experimentsControls.setFilterModel}
                  rowCount={totalExperimentsCount}
                  initialState={{
                    columns: {
                      columnVisibilityModel: {
                        first_timestamp: true,
                        last_timestamp: true,
                      },
                    },
                  }}
                  disableColumnSelector={false}
                />
              )}
            </Paper>
          </Grid>
        </Activity>
        <Activity mode={selectedExperiment ? 'visible' : 'hidden'}>
          <TabContext
            value={selectedTab}
            keepMounted
            style={{ height: 500, width: '100%' }}
          >
            <Grid size={12} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList
                onChange={handleChange}
                aria-label="Data Item selection for manual trigger"
              >
                {temporalGroups.map((key, index) => {
                  const isDisabled = {}

                  isDisabled['disabled'] =
                    rowSelectionModel?.ids?.size && selectedTab !== key
                      ? 'disabled'
                      : ''

                  return (
                    <Tab
                      key={key}
                      label={key}
                      value={key}
                      icon={
                        key === 'file' ? (
                          <SourceTwoTone />
                        ) : (
                          <DoubleArrowTwoTone />
                        )
                      }
                      sx={{ color: '#2d3748' }}
                      iconPosition="end"
                      {...isDisabled}
                    />
                  )
                })}
              </TabList>
            </Grid>

            {temporalGroups.map((key) => {
              return (
                <TabPanel
                  key={key}
                  label={key}
                  value={key}
                  style={{ width: '100%' }}
                >
                  {/* temporal groups */}
                  <Grid size={12}>
                    <HoloTable
                      title={key}
                      id={`temporal-group-${key}`}
                      data={rawTemporalGroupsData}
                      columns={temporalGroupColumns}
                      paginationModel={
                        rawTemporalGroupsDataTableControls.paginationModel
                      }
                      onPaginationModelChange={
                        rawTemporalGroupsDataTableControls.setPaginationModel
                      }
                      sortModel={rawTemporalGroupsDataTableControls.sortModel}
                      onSortModelChange={
                        rawTemporalGroupsDataTableControls.setSortModel
                      }
                      filterModel={
                        rawTemporalGroupsDataTableControls.filterModel
                      }
                      onFilterModelChange={
                        rawTemporalGroupsDataTableControls.setFilterModel
                      }
                      rowCount={rawTemporalGroupsDataTotalItemsCount}
                      handleDownload={() => {}}
                      checkboxSelection={true}
                      onRowClick={() => {}}
                      disableColumnResize
                      onRowSelectionModelChange={(newRowSelectionModel) => {
                        setRowSelectionModel(newRowSelectionModel)
                      }}
                      rowSelectionModel={rowSelectionModel}
                      keepNonExistentRowsSelected
                      disableRowSelectionExcludeModel
                    />
                  </Grid>
                </TabPanel>
              )
            })}
          </TabContext>
        </Activity>

        <Activity mode={rowSelectionModel?.ids?.size ? 'visible' : 'hidden'}>
          <Grid size={12}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth sx={{ mb: 1 }}>
                  <InputLabel id="analysis-chain-selector">
                    Analysis Chain
                  </InputLabel>
                  <Select
                    labelId="analysis-chain-selector"
                    multiple
                    value={selectedAnalysisChain}
                    onChange={(e) => {
                      const {
                        target: { value },
                      } = e
                      setSelectedAnalysisChain(
                        typeof value === 'string' ? value.split(',') : value,
                      )
                    }}
                    input={<OutlinedInput label="Analysis Chain" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const chain = analysisChains.find(
                            (c) => c._id === value,
                          )
                          return (
                            <Chip
                              key={value}
                              label={chain.name || chain._id}
                              onDelete={handleDeleteChip(value)}
                            />
                          )
                        })}
                      </Box>
                    )}
                  >
                    {analysisChains.map((chain) => (
                      <MenuItem key={chain._id} value={chain._id}>
                        {chain.name || chain._id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid container size={12}>
            <LoadingButton
              variant="contained"
              onClick={handleSubmit}
              loading={isSubmitting}
            >
              Trigger
            </LoadingButton>
          </Grid>
        </Activity>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <MuiAlert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Grid>
    </LocalizationProvider>
  )
}
